import {
  HHCheckbox,
  HHFormControlLabel,
  HHSelect,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ClientType, PartnershipType } from '../../types';
import {
  EligibilitySearchDepth,
  InsurerType,
  ToolRunMode,
} from './tool-validation-schema';

interface BillToolFormProps {
  clientOptions: ClientType[];
  insurerOptions: InsurerType[];
  partnershipOptions: PartnershipType[];
  paymentTypeOptions: { label: string; value: string }[];
  billingTypeOptions: { value: string; displayName: string }[];
  isFilterLoading: boolean;
}

const BillToolForm = ({
  clientOptions,
  insurerOptions,
  partnershipOptions,
  paymentTypeOptions,
  billingTypeOptions,
  isFilterLoading,
}: BillToolFormProps): JSX.Element => {
  const {
    control,
    watch,
    resetField,
    trigger,
    formState: { errors },
  } = useFormContext();

  const [startDate, endDate] = watch(['startDate', 'endDate']);

  useEffect(() => {
    trigger(['startDate', 'endDate']);
  }, [startDate, endDate, trigger]);

  return (
    <HHStack spacing={6} direction="column">
      <HHStack spacing={6} direction={{ xs: 'column', md: 'row' }}>
        <HHStack spacing={6} direction="column" flex={1}>
          <Controller
            control={control}
            name="runMode"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHSelect
                label="Run Mode"
                initialSelectValue={value}
                selectOptions={Object.values(ToolRunMode).map(value => ({
                  value,
                  displayName: value,
                }))}
                sx={{ textTransform: 'capitalize' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        textTransform: 'capitalize',
                      },
                    },
                  },
                }}
                onChange={(e): void => {
                  onChange(e.target.value);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="partnership"
            render={({ field: { onChange, value } }): JSX.Element => (
              <Autocomplete
                value={value || null}
                options={partnershipOptions}
                getOptionLabel={(option: PartnershipType): string =>
                  option.name
                }
                isOptionEqualToValue={(
                  option: PartnershipType,
                  value: PartnershipType,
                ): boolean =>
                  option.id === value.id || option.name === value.name
                }
                filterOptions={(options, params): PartnershipType[] =>
                  options.filter(
                    option =>
                      option.name
                        .toLowerCase()
                        .indexOf(params.inputValue.toLowerCase()) !== -1 ||
                      option.id.toString().indexOf(params.inputValue) !== -1,
                  )
                }
                size="small"
                onChange={(_, newValue: PartnershipType | null): void => {
                  resetField('client', { defaultValue: null });
                  resetField('insurer', { defaultValue: null });
                  onChange(newValue);
                }}
                fullWidth
                disabled={isFilterLoading}
                sx={{ minWidth: 230 }}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    hhVariant="variant-bypass"
                    variant="outlined"
                    label={'Search by partnership name or id'}
                  />
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="client"
            render={({ field: { onChange, value } }): JSX.Element => (
              <Autocomplete
                value={value || null}
                options={clientOptions}
                getOptionLabel={(option: ClientType): string => option.name}
                disabled={isFilterLoading}
                isOptionEqualToValue={(
                  option: ClientType,
                  value: ClientType,
                ): boolean =>
                  option.id === value.id || option.name === value.name
                }
                filterOptions={(options, params): ClientType[] =>
                  options.filter(
                    option =>
                      option.name
                        .toLowerCase()
                        .indexOf(params.inputValue.toLowerCase()) !== -1 ||
                      option.id.toString().indexOf(params.inputValue) !== -1,
                  )
                }
                size="small"
                onChange={(_, newValue: ClientType | null): void => {
                  resetField('insurer', { defaultValue: null });
                  onChange(newValue);
                }}
                fullWidth
                sx={{ minWidth: 230 }}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    hhVariant="variant-bypass"
                    variant="outlined"
                    label={'Search by Client name or id'}
                  />
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="insurer"
            render={({ field: { onChange, value } }): JSX.Element => (
              <Autocomplete
                value={value || null}
                options={insurerOptions}
                getOptionLabel={(option: InsurerType): string => option.name}
                isOptionEqualToValue={(
                  option: InsurerType,
                  value: InsurerType,
                ): boolean =>
                  option.id === value.id || option.name === value.name
                }
                filterOptions={(options, params): InsurerType[] =>
                  options.filter(
                    option =>
                      option.name
                        .toLowerCase()
                        .indexOf(params.inputValue.toLowerCase()) !== -1 ||
                      option.id.toString().indexOf(params.inputValue) !== -1,
                  )
                }
                size="small"
                disabled={isFilterLoading}
                onChange={(_, newValue: InsurerType | null): void =>
                  onChange(newValue)
                }
                fullWidth
                sx={{ minWidth: 230 }}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    hhVariant="variant-bypass"
                    variant="outlined"
                    label={'Search by insurer name or id'}
                  />
                )}
              />
            )}
          />
        </HHStack>
        <HHStack spacing={6} direction="column" flex={1}>
          <Controller
            control={control}
            name="paymentTypes"
            render={({ field: { onChange, value } }): JSX.Element => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={paymentTypeOptions}
                value={paymentTypeOptions.filter(option =>
                  value?.includes(option.value),
                )}
                onChange={(_, newValue): void => {
                  onChange(
                    newValue.length > 0
                      ? newValue.map(option => option.value)
                      : null,
                  );
                }}
                renderOption={(props, option, { selected }): JSX.Element => (
                  <li {...props}>
                    <HHCheckbox
                      checked={selected}
                      color="primary"
                      hhVariant="variant-bypass"
                    />
                    {option.label}
                  </li>
                )}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    hhVariant="variant-bypass"
                    variant="outlined"
                    label="Payment Types"
                  />
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="claimsLimit"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHTextField
                hhVariant="variant-bypass"
                label="Claims Limit"
                value={value}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  min: 0,
                }}
                fullWidth
                onChange={(e): void => {
                  onChange(e.target.value);
                }}
                type="number"
              />
            )}
          />
          <Controller
            control={control}
            name="eligibilitySearchDepth"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHSelect
                label="Eligibility Search Depth"
                initialSelectValue={value}
                selectOptions={Object.values(EligibilitySearchDepth).map(
                  value => ({
                    value,
                    displayName: value,
                  }),
                )}
                sx={{ textTransform: 'capitalize' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        textTransform: 'capitalize',
                      },
                    },
                  },
                }}
                onChange={(e): void => {
                  onChange(e.target.value);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="billingType"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHSelect
                label="Billing Type"
                id="billing-type-select"
                initialSelectValue={value || null}
                selectOptions={billingTypeOptions ?? []}
                disabled={isFilterLoading}
                sx={{ textTransform: 'capitalize' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        textTransform: 'capitalize',
                      },
                    },
                  },
                }}
                onChange={(e): void => {
                  onChange(e.target.value);
                }}
              />
            )}
          />
        </HHStack>
      </HHStack>
      <HHStack spacing={6} direction={{ xs: 'column', md: 'row' }}>
        <Controller
          control={control}
          name="startDate"
          render={({ field: { onChange, value } }): JSX.Element => (
            <DatePicker
              label="Start Date"
              value={value}
              onChange={(newValue): void => onChange(newValue)}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  fullWidth
                  hhVariant="variant-bypass"
                  label="Start Date"
                  error={
                    params.error ||
                    Object.keys(errors?.startDate ?? {}).length !== 0
                  }
                  helperText={errors.startDate?.message as string}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="endDate"
          render={({ field: { onChange, value } }): JSX.Element => (
            <DatePicker
              label="End Date"
              value={value}
              onChange={(newValue): void => onChange(newValue)}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  fullWidth
                  hhVariant="variant-bypass"
                  label="End Date"
                  error={
                    params.error ||
                    Object.keys(errors?.endDate ?? {}).length !== 0
                  }
                  helperText={errors.endDate?.message as string}
                />
              )}
            />
          )}
        />
      </HHStack>
      <HHStack direction="row" justifyContent="flex-end">
        <HHFormControlLabel
          label="Exclude Partnership"
          control={
            <Controller
              control={control}
              name="excludePartnership"
              render={({ field: { onChange, value } }): JSX.Element => (
                <HHCheckbox
                  sx={{ color: 'gray' }}
                  checked={value}
                  onChange={(e): void => onChange(e)}
                  hhVariant="primary"
                />
              )}
            />
          }
        />
        <HHFormControlLabel
          label="Run Admin Fee Claims"
          control={
            <Controller
              control={control}
              name="runAdminFeeClaims"
              render={({ field: { onChange, value } }): JSX.Element => (
                <HHCheckbox
                  sx={{ color: 'gray' }}
                  checked={value}
                  onChange={onChange}
                  hhVariant="primary"
                />
              )}
            />
          }
        />
      </HHStack>
    </HHStack>
  );
};

export default BillToolForm;
